'use client';

import React, { useState, useEffect, Suspense } from 'react';
import classes from './ListingInformation.module.css';
import Fuse from 'fuse.js';
import locations from '@/utils/locations';
import { colorArray } from '@/utils/Colors';
import getVelouDisplayName from '@/utils/getVelouDisplayName';
import ConditionModal from '../../../../../components/modals/product-related/ConditionModal';
import { useAuthContext } from '@/context/';
import { useRouter } from 'next/navigation';
import { Armchair, BedDouble } from 'lucide-react';
import { Info } from 'react-feather';
import axios from 'axios';

const ListingInformation = (props) => {
	const { listing, matchedHierarchy, windowSize, deviceWidth } = props;

	const router = useRouter();
	const { auth } = useAuthContext();

	const isAuthor =
		auth.isAuthenticated && auth?.user?._id === listing?.author?._id;

	const [showConditionsModal, setShowConditionsModal] = useState(false);
	const [showExtraInfo, setShowExtraInfo] = useState(false);
	const cleanedExtraInfo = listing.extraInfo
		? listing.extraInfo.replace(/â¢/g, '• ')
		: '';

	const locationHandler = (location) => {
		if (location.text) {
			if (
				location.text === 'Alameda County' ||
				location.text.includes('San Francisco')
			) {
				location.text = 'Bay Area, California';
			} else if (location.text === 'California') {
				location.text = 'Los Angeles, California';
			} else if (location.text === 'Fairfax County') {
				location.text = 'Philadelphia, Pennsylvania';
			} else if (location.text === 'Lee County') {
				location.text = 'Fort Myers, Florida';
			} else if (location.text === 'Maricopa County') {
				location.text = 'Phoenix, Arizona';
			} else if (location.text === 'El Paso County') {
				location.text = 'Colorado Springs, Colorado';
			}

			const fuse = new Fuse(locations, {
				keys: ['area', 'geolocation'],
				threshold: 0.8,
				findAllMatches: true,
				ignoreLocation: true,
			});
			const res = fuse.search(location.text).map((result) => result.item);
			if (res.length) {
				router.push(`/location/${res[0].id.toLowerCase()}`);
			}
		}
	};

	const isClickableKey = (key) => {
		const clickableKeys = [
			'designer',
			'category',
			'artist',
			'design',
			'room',
			'rooms',
			'product_color',
			'shape',
			'type',
			'usage',
			'style',
			'styles',
			'material',
			'materials',
			'country',
			'product_features',
		]; // Adjust this list as needed
		return clickableKeys.includes(key);
	};

	const searchHandler = (key, tag) => {
		let queryParams;
		switch (key) {
			case 'category':
				queryParams = `/collections/${tag
					.replace(/ /g, '-')
					.replace(/&/g, 'and')
					.toLowerCase()}`;
				break;
			case 'type':
				queryParams = `/collections/${matchedHierarchy?.category
					.replace(/ /g, '-')
					.replace(/&/g, 'and')
					.toLowerCase()}/${tag
					.replace(/ /g, '-')
					.replace(/&/g, 'and')
					.toLowerCase()}`;
				break;
			case 'design':
				queryParams = `/collections/${matchedHierarchy?.category
					.replace(/ /g, '-')
					.replace(/&/g, 'and')
					.toLowerCase()}/${matchedHierarchy?.subCategory
					.replace(/ /g, '-')
					.replace(/&/g, 'and')
					.toLowerCase()}/${tag
					.replace(/ /g, '-')
					.replace(/&/g, 'and')
					.toLowerCase()}`;
				break;
			case 'material':
				queryParams = `/collections?material=${tag}`;
				break;
			case 'materials':
				queryParams = `/collections?material=${tag}`;
				break;
			case 'style':
				queryParams = `/style/${tag}`;
				break;
			case 'styles':
				queryParams = `/style/${tag}`;
				break;
			case 'room':
				queryParams = `/room/${tag}`;
				break;
			case 'rooms':
				queryParams = `/room/${tag}`;
				break;
			case 'design':
				queryParams = `/collections?design=${capitalizeEachWord(tag)}`;
				break;
			case 'designer':
				queryParams = `/collections?keyword=${capitalizeEachWord(tag)}`;
				break;
			case 'brand':
				queryParams = `/brand/${tag
					.replace(/ /g, '-')
					.replace(/&/g, 'and')
					.toLowerCase()}`;
				break;
			case 'product_features':
				queryParams = `/collections?product_features=${tag}`;
				break;
			case 'tags':
				queryParams = `/collections?tags=${tag}`;
				break;
			case 'product_color':
				queryParams = `/collections?color=${capitalizeEachWord(tag)}`;
				break;
			default:
				queryParams = `/collections?keyword=${capitalizeEachWord(tag)}`;
		}

		router.push(queryParams);
	};

	const fixEncoding = (str) => {
		return str
			.toString()
			.replace(/Ã¸/g, 'ø')
			.replace(/Ã/g, 'Ó')
			.replace(/Ã¦/g, 'æ')
			.replace(/Ã¥/g, 'å')
			.replace(/Ã¶/g, 'ö')
			.replace(/Ã¸/g, 'ø')
			.replace(/Ã/g, 'Ó')
			.replace(/Ã¦/g, 'æ')
			.replace(/Ã¥/g, 'å');

		// Add more replacements as needed
	};

	const capitalizeEachWord = (text) => {
		// Direct replacement for special cases
		const specialCases = {
			'1980_s': '1980s',
			'1990_s': '1990s',
			'20_th_century': '20th Century',
		};

		if (specialCases[text]) {
			return specialCases[text];
		}

		// For words that aren't special cases
		return text
			.replace(/_/g, ' ')
			.split(' ')
			.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
			.join(' ');
	};

	const [decodedText, setDecodedText] = useState('');

	const parser = typeof window !== 'undefined' ? new DOMParser() : null;
	useEffect(() => {
		const decodedText =
			cleanedExtraInfo.toString().replace(/<\/?[^>]+>/gi, ' ').length > 300 &&
			!showExtraInfo
				? `${fixEncoding(cleanedExtraInfo.toString())
						.replace(/<\/?[^>]+>/gi, ' ')
						.slice(0, 300)}...`
				: fixEncoding(cleanedExtraInfo.toString()).replace(/<\/?[^>]+>/gi, ' ');

		setDecodedText(
			parser
				? parser.parseFromString(
						'<!doctype html><body>' + decodedText,
						'text/html'
				  ).body.textContent
				: ''
		);
	}, [showExtraInfo, cleanedExtraInfo]);

	const [windowWidth, setWindowWidth] = useState(deviceWidth);

	useEffect(() => {
		// This will only run on the client-side
		const handleResize = () => {
			setWindowWidth(window.innerWidth);
		};

		// Set initial window width
		handleResize();

		// Add event listener to handle window resize
		window.addEventListener('resize', handleResize);

		// Cleanup the event listener on component unmount
		return () => {
			window.removeEventListener('resize', handleResize);
		};
	}, []);

	if (windowSize?.lt && windowWidth > windowSize?.lt) {
		return;
	} else if (windowSize?.gt && windowWidth < windowSize?.gt) {
		return;
	}

	const updateViews = async () => {
		if (listing?._id) {
			await axios.get(
				`${process.env.NEXT_PUBLIC_SERVER_API}/api/listings/viewed/${listing._id}`
			);
		}
	};

	useEffect(() => {
		updateViews();
	}, [listing]);

	return (
		<>
			<div className={classes.mainContainer}>
				{(listing.measurements?.H ||
					listing.velouAttributes?.height ||
					listing.measurements?.L ||
					listing.velouAttributes?.length ||
					listing.measurements?.D ||
					listing.velouAttributes?.width) && (
					<>
						{windowWidth < 600 && (
							<h3 className={classes.keyMain}>Item Information</h3>
						)}

						<div className={classes.dimensionsItem}>
							<>
								{listing.measurements?.H || listing.velouAttributes?.height ? (
									<section className={classes.dimensionsRow}>
										<div className={classes.dimensionsBox}>
											<span className={classes.height} />{' '}
										</div>
										<section className={classes.descMain}>
											<h3 className={classes.key}>Height</h3>
											<div className={classes.value}>
												{listing.measurements?.H
													? listing.measurements?.H
													: listing.velouAttributes?.height
													? getVelouDisplayName(listing.velouAttributes.height)
													: ''}{' '}
												inches
											</div>
										</section>
									</section>
								) : (
									''
								)}
								{listing.measurements?.L || listing.velouAttributes?.length ? (
									<section className={classes.dimensionsRow}>
										<div className={classes.dimensionsBox}>
											<span className={classes.width} />
										</div>
										<section className={classes.descMain}>
											<h3 className={classes.key}>Width</h3>
											<div className={classes.value}>
												{listing.measurements?.L
													? listing.measurements?.L
													: listing.velouAttributes?.length
													? getVelouDisplayName(listing.velouAttributes.length)
													: ''}{' '}
												inches
											</div>
										</section>
									</section>
								) : (
									''
								)}
								{listing.measurements?.D || listing.velouAttributes?.width ? (
									<section className={classes.dimensionsRow}>
										<div className={classes.dimensionsBox}>
											<span className={classes.depth} />
										</div>
										<section className={classes.descMain}>
											<h3 className={classes.key}>Depth</h3>
											<div className={classes.value}>
												{listing.measurements?.D
													? listing.measurements?.D
													: listing.velouAttributes?.width
													? getVelouDisplayName(listing.velouAttributes.width)
													: ''}{' '}
												inches
											</div>
										</section>
									</section>
								) : (
									''
								)}
								{(listing.bedSize || listing.velouAttributes?.size) && (
									<section className={classes.dimensionsRow}>
										<div className={classes.dimensionsBox}>
											<BedDouble className={classes.dimIcon} />
										</div>
										<section className={classes.descMain}>
											<h3 className={classes.key}>Bed Size</h3>
											<div className={classes.value}>
												{listing.bedSize
													? listing.bedSize
													: listing.velouAttributes?.size
													? getVelouDisplayName(
															listing.velouAttributes?.size[0]
													  )
													: ''}
											</div>
										</section>
									</section>
								)}

								{listing.seatHeight && (
									<section className={classes.dimensionsRow}>
										<div className={classes.dimensionsBox}>
											<Armchair className={classes.dimIcon} />
										</div>
										<section className={classes.descMain}>
											<h3 className={classes.key}>Seat Height</h3>
											<div className={classes.value}>
												{listing.seatHeight} inches
											</div>
										</section>
									</section>
								)}
							</>
						</div>
					</>
				)}
				{listing.extraInfo &&
					cleanedExtraInfo &&
					typeof cleanedExtraInfo == 'string' && (
						<section className={classes.descMain}>
							<h3 className={classes.key}>Description</h3>

							<p
								className={`${classes.description} ${classes.showAll}`}
								dangerouslySetInnerHTML={{
									__html: decodedText,
								}}
							/>

							{cleanedExtraInfo.toString().replace(/<\/?[^>]+>/gi, ' ').length >
								300 && (
								<button
									className={classes.readMore}
									onClick={() => setShowExtraInfo(!showExtraInfo)}
								>
									{showExtraInfo ? 'Read Less' : 'Read More'}
								</button>
							)}
						</section>
					)}
				<ul className={classes.list}>
					{listing.brand &&
					listing.brand !== 'undefined' &&
					listing.brand !== 'null' ? (
						<div className={classes.listItem}>
							<h3 className={classes.key}>Brand</h3>
							<section className={classes.flexRow}>
								<span
									className={classes.value1}
									onClick={() => searchHandler('brand', listing.brand)}
								>
									{fixEncoding(listing.brand)}
								</span>
							</section>
						</div>
					) : listing.velouAttributes?.manufacturer?.length > 0 ? (
						<div className={classes.listItem}>
							<h3 className={classes.key}>Brand</h3>
							<section className={classes.flexRow}>
								<span
									className={classes.value1}
									onClick={() =>
										searchHandler(
											'brand',
											listing.velouAttributes?.manufacturer[0]
												?.replace(/_/g, ' ')
												?.split(' ')
												?.map(
													(word) => word.charAt(0).toUpperCase() + word.slice(1)
												)
												?.join(' ')
										)
									}
								>
									{getVelouDisplayName(
										listing.velouAttributes?.manufacturer[0]
									)}
								</span>
							</section>
						</div>
					) : listing.velouAttributes?.brands?.length > 0 ? (
						<div className={classes.listItem}>
							<h3 className={classes.key}>Brand</h3>
							<section className={classes.flexRow}>
								<span
									className={classes.value1}
									onClick={() =>
										searchHandler(
											'brand',
											listing.velouAttributes?.brands[0]
												?.replace(/_/g, ' ')
												?.split(' ')
												?.map(
													(word) => word.charAt(0).toUpperCase() + word.slice(1)
												)
												?.join(' ')
										)
									}
								>
									{getVelouDisplayName(listing.velouAttributes?.brands[0])}
								</span>
							</section>
						</div>
					) : (
						''
					)}
					{listing.designer ? (
						<div className={classes.listItem}>
							<h3 className={classes.key}>Designer</h3>
							<section className={classes.flexRow}>
								<span
									className={classes.value1}
									onClick={() => searchHandler('designer', listing.designer)}
								>
									{fixEncoding(listing.designer)}
								</span>
							</section>
						</div>
					) : listing.velouAttributes?.designer?.length > 0 ? (
						<div className={classes.listItem}>
							<h3 className={classes.key}>Designer</h3>
							<section className={classes.flexRow}>
								<span
									className={classes.value1}
									onClick={() =>
										searchHandler(
											'designer',
											listing.velouAttributes?.designer[0]
												?.replace(/_/g, ' ')
												?.split(' ')
												?.map(
													(word) => word.charAt(0).toUpperCase() + word.slice(1)
												)
												?.join(' ')
										)
									}
								>
									{getVelouDisplayName(listing.velouAttributes?.designer[0])}
								</span>
							</section>
						</div>
					) : (
						''
					)}

					{listing.velouAttributes
						? Object.keys(listing.velouAttributes)
								.map((key) => {
									return {
										key: key,
										values: listing.velouAttributes[key],
									};
								})
								?.map((item, idx) =>
									item.key === 'materials' ||
									item.key === 'table_base' ||
									item.key === 'country' ||
									item.key === 'rooms' ||
									item.key === 'product_features' ||
									item.key === 'styles' ? (
										<div key={idx} className={classes.listItem}>
											<h3 className={classes.key}>
												{getVelouDisplayName(item.key)}
											</h3>
											<div className={classes.flexRow}>
												{item.values.map((value, valueIdx) => (
													<p
														key={valueIdx}
														className={classes.value1}
														onClick={
															isClickableKey(item.key)
																? () => searchHandler(item.key, value)
																: null
														}
													>
														{getVelouDisplayName(value)}
													</p>
												))}
											</div>
										</div>
									) : (
										''
									)
								)
						: ''}

					<div
						className={classes.listItem}
						style={{ cursor: 'pointer' }}
						onClick={() => setShowConditionsModal(true)}
					>
						<h3 className={classes.key}>
							Wear Condition <Info className={classes.infoIcon} />
						</h3>
						<div className={classes.value}>{listing.condition}</div>
					</div>

					{listing.velouAttributes?.product_color?.length > 0 ? (
						<div className={classes.listItem}>
							<h3 className={classes.key}>Colors</h3>
							<div className={classes.flexRow}>
								{listing.velouAttributes?.product_color.map(
									(color, idx, arr) => {
										const closestColor = colorArray.find(
											(i) => i.name.toLowerCase() === color.toLowerCase()
										);

										if (closestColor) {
											return (
												<React.Fragment key={idx}>
													<div
														className={classes.colorFilterBox}
														onClick={() =>
															router.push(
																`/collections?color=${closestColor?.name}`
															)
														}
													>
														<div
															className={classes.colorRound}
															style={{
																background: closestColor?.code,
															}}
														/>
														<div className={classes.value}>
															{closestColor?.name}
														</div>
													</div>
												</React.Fragment>
											);
										} else {
											return (
												<div className={classes.value} key={idx}>
													{color}
												</div>
											);
										}
									}
								)}
							</div>
						</div>
					) : (
						''
					)}

					{listing.tags && listing.tags.length > 0 && (
						<div className={classes.listItem}>
							<h3 className={classes.key}>Tags</h3>
							<div className={classes.flexRow}>
								{listing.tags
									?.filter((i) => i.length < 20)
									.slice(0, 8)
									.map(
										(tag, idx) =>
											tag &&
											typeof tag === 'string' && (
												<div
													className={classes.value1}
													key={idx}
													onClick={() =>
														searchHandler(
															'tags',
															tag
																.replace(/"/g, '')
																.toLowerCase()
																.charAt(0)
																.toUpperCase() +
																tag.replace(/"/g, '').toLowerCase().slice(1)
														)
													}
												>
													{tag
														.replace(/"/g, '')
														.toLowerCase()
														.charAt(0)
														.toUpperCase() +
														tag.replace(/"/g, '').toLowerCase().slice(1)}
												</div>
											)
									)}
							</div>
						</div>
					)}

					{listing.quantity && (
						<div className={classes.listItem}>
							<h3 className={classes.key}>Quantity</h3>
							<div className={classes.value}>{listing.quantity} available</div>
						</div>
					)}
					{listing.delivery?.location?.context && (
						<div className={classes.listItem1}>
							<h3 className={classes.key}>Location</h3>
							<div className={classes.flexRow}>
								<div
									className={classes.descTabs}
									onClick={() =>
										locationHandler(listing.delivery.location.context[3])
									}
								>
									<div className={classes.value1}>
										{listing.delivery.location?.context[3]?.text}
										{' - '}
										{listing.delivery.location?.context[2]?.text}
										{' - '}
										{listing.delivery.location?.context[1]?.text}
									</div>
								</div>
							</div>
						</div>
					)}

					{listing.SKU && isAuthor && (
						<div className={classes.listItem}>
							<h3 className={classes.key}>SKU</h3>
							<div className={classes.value}>{listing.SKU}</div>
						</div>
					)}
				</ul>
			</div>

			<Suspense fallback={<></>}>
				<ConditionModal
					show={showConditionsModal}
					onHide={() => setShowConditionsModal(false)}
				/>
			</Suspense>
		</>
	);
};

export default ListingInformation;
